import React, { useContext, useMemo } from 'react';

import { SetlistStatusEnum } from '@/components/SetlistSummaryCard/utils';
import {
	ReportCardCarousel,
	ReportCardCarouselItem,
} from '@/components/shared/ReportCardCarousel';
import { SettingSectionTitle } from '@/components/shared/SettingCard';
import { GetBiblePassageAnalysisMutation } from '@/graphql';
import { ChartSectionData, makeSection } from '@/helpers/reportsDataHelpers';
import { TagTrendPoint } from '@/hooks/useReportsData';
import { DistributionCirclePackingChart } from '@/pages/Reports/components/DistributionCirclePackingChart/DistributionCirclePackingChart';
import { useTeamSongs } from '@/providers/TeamSongsProvider';

import { GenerateSetlistContext } from '../../GenerateSetlist';
import BibleVerseEmotions from '../BibleVerseEmotions/BibleVerseEmotions';
import EnergyFlow from '../EnergyFlow/EnergyFlow';
import FeedbackLineChart from '../FeedbackLineChart/FeedbackLineChart';
import GospelFlow from '../GospelFlow/GospelFlow';
import LoadingCard from '../LoadingCard/LoadingCard';
import QuartilesReport from '../QuartilesReport/QuartilesReport';

const SetlistAnalysis: React.FC<{
	variant?: 'list' | 'carousel';
	isLoading?: boolean;
	biblePassageAnalysis?:
		| GetBiblePassageAnalysisMutation['getBiblePassageAnalysis']
		| undefined;
	reference?: string;
	teamSongIds: string[];
	postSermonSongPosition?: number | null;
}> = ({
	variant = 'carousel',
	isLoading = false,
	biblePassageAnalysis,
	reference,
	teamSongIds,
	postSermonSongPosition,
}) => {
	const { setlist, status, isGenerateLoading } = useContext(
		GenerateSetlistContext,
	);

	const { findTeamSongsById } = useTeamSongs();

	const distributions = useMemo<{
		theme: ChartSectionData<TagTrendPoint>;
		theology: ChartSectionData<TagTrendPoint>;
		gospelStory: ChartSectionData<TagTrendPoint>;
		emotion: ChartSectionData<TagTrendPoint>;
	}>(() => {
		const songs = findTeamSongsById(teamSongIds).filter(
			Boolean,
		) as NonNullable<ReturnType<typeof findTeamSongsById>[0]>[];

		const theme: TagTrendPoint[] = [];
		const theology: TagTrendPoint[] = [];
		const gospelStory: TagTrendPoint[] = [];
		const emotion: TagTrendPoint[] = [];

		for (const song of songs) {
			for (const kw of song.analysisOutput?.thematicKeywords ?? []) {
				if (kw.theme && kw.strength != null)
					theme.push({
						setlistId: song.id,
						date: song.name,
						label: kw.theme,
						count: kw.strength,
					});
			}
			for (const kw of song.analysisOutput?.theologicalKeywords ?? []) {
				if (kw.theology && kw.strength != null)
					theology.push({
						setlistId: song.id,
						date: song.name,
						label: kw.theology,
						count: kw.strength,
					});
			}
			for (const tag of song.gospelTags ?? []) {
				gospelStory.push({
					setlistId: song.id,
					date: song.name,
					label: tag,
					count: 1,
				});
			}
			for (const e of song.analysisOutput?.emotions ?? []) {
				if (e.emotion && e.strength != null)
					emotion.push({
						setlistId: song.id,
						date: song.name,
						label: e.emotion,
						count: e.strength,
					});
			}
		}

		return {
			theme: makeSection(theme, theme),
			theology: makeSection(theology, theology),
			gospelStory: makeSection(gospelStory, gospelStory),
			emotion: makeSection(emotion, emotion),
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [teamSongIds.join(','), findTeamSongsById]);

	const isSetlistComplete =
		!!setlist &&
		(status === SetlistStatusEnum.COMPLETE ||
			status === SetlistStatusEnum.IN_REVIEW);

	const reports = useMemo(
		() =>
			[
				isLoading && {
					id: 'Loading',
					component: <LoadingCard />,
				},
				setlist &&
					isSetlistComplete && {
						id: 'feedback',
						component: <FeedbackLineChart setlist={setlist} />,
					},
				setlist &&
					isSetlistComplete && {
						id: 'quartiles',
						component: <QuartilesReport setlist={setlist} />,
					},
				variant === 'carousel' &&
					biblePassageAnalysis &&
					reference &&
					postSermonSongPosition && {
						id: 'bibleVerse',
						component: (
							<BibleVerseEmotions
								biblePassageAnalysis={biblePassageAnalysis}
								reference={reference}
								songId={teamSongIds[postSermonSongPosition - 1]}
							/>
						),
					},
				{
					id: 'gospelFlow',
					component: (
						<GospelFlow
							teamSongIds={teamSongIds}
							className="mt-sm"
						/>
					),
				},
				{
					id: 'energyFlow',
					component: <EnergyFlow teamSongIds={teamSongIds} />,
				},
			].filter(Boolean) as ReportCardCarouselItem[],
		[
			isLoading,
			setlist,
			variant,
			biblePassageAnalysis,
			reference,
			postSermonSongPosition,
			teamSongIds,
			isSetlistComplete,
		],
	);

	if (variant === 'list') {
		const feedbackReports = reports.filter((r) =>
			['feedback', 'quartiles'].includes(r.id),
		);
		const flowReports = reports.filter((r) =>
			['gospelFlow', 'energyFlow'].includes(r.id),
		);
		const contentReports = reports.filter((r) =>
			['bibleVerse'].includes(r.id),
		);

		return (
			<>
				{feedbackReports.length > 0 && (
					<>
						<SettingSectionTitle type="title" title="Feedback" />
						{feedbackReports.map((report) => (
							<div key={report.id}>{report.component}</div>
						))}
					</>
				)}
				<SettingSectionTitle type="title" title="Content" />
				{contentReports.map((report) => (
					<div key={report.id}>{report.component}</div>
				))}
				<DistributionCirclePackingChart
					title="Themes"
					section={distributions.theme}
					perSetlist={true}
					defaultView="bubble"
				/>
				<DistributionCirclePackingChart
					title="Theology"
					section={distributions.theology}
					perSetlist={true}
					defaultView="word-cloud"
				/>
				<DistributionCirclePackingChart
					title="Gospel Story"
					section={distributions.gospelStory}
					perSetlist={true}
					defaultView="rank"
					semanticColors
				/>
				<DistributionCirclePackingChart
					title="Emotions"
					section={distributions.emotion}
					perSetlist={true}
					semanticColors
					defaultView="bubble"
				/>
				{flowReports.length > 0 && (
					<>
						<SettingSectionTitle type="title" title="Flow" />
						{flowReports.map((report) => (
							<div key={report.id}>{report.component}</div>
						))}
					</>
				)}
			</>
		);
	}
	return <ReportCardCarousel reports={reports} />;
};

export default SetlistAnalysis;
